import { getWeekDateRange, parseWeekSeries } from './timeUtils';
import { getWeekNumber } from './helpers';

export const inferEndWeekYear = (startYear, startWeek, endWeek) => (
    endWeek >= startWeek ? startYear : startYear + 1
);

export const PERIOD_COLOR_PALETTE = [
    '#fecaca', '#fca5a5', '#fecdd3', '#f9a8d4',
    '#fed7aa', '#fdba74', '#fef08a', '#fde047',
    '#d9f99d', '#a7f3d0', '#a5f3fc', '#6ee7b7',
    '#bae6fd', '#c7d2fe', '#e9d5ff', '#ddd6fe'
];

export const pickDefaultPeriodColor = () => (
    PERIOD_COLOR_PALETTE[Math.floor(Math.random() * PERIOD_COLOR_PALETTE.length)]
);

const MONTH_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', { month: 'short', year: '2-digit' });

export const getPeriodStartEndDates = (period) => {
    if (!period) return null;

    const startDateRaw = period.start_date ?? period.startDate;
    const endDateRaw = period.end_date ?? period.endDate;
    if (startDateRaw && endDateRaw) {
        const startDate = new Date(startDateRaw);
        const endDate = new Date(endDateRaw);
        if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
            return { startDate, endDate };
        }
    }

    const sy = period.startWeekYear ?? period.start_week_year ?? period.year;
    const sw = period.startWeek ?? period.start_week;
    const ew = period.endWeek ?? period.end_week;
    if (sy == null || sw == null || ew == null) return null;

    const ey = period.endWeekYear ?? period.end_week_year ?? inferEndWeekYear(sy, sw, ew);
    const startRange = getWeekDateRange(sy, sw);
    const endRange = getWeekDateRange(ey, ew);
    if (!startRange || !endRange) return null;

    return { startDate: startRange.startDate, endDate: endRange.endDate };
};

export const formatPeriodShortMonthYearRange = (period) => {
    const dates = getPeriodStartEndDates(period);
    if (!dates) return '';

    const start = MONTH_YEAR_FORMATTER.format(dates.startDate);
    const end = MONTH_YEAR_FORMATTER.format(dates.endDate);
    if (start === end) return start;
    return `${start} – ${end}`;
};

export const formatPeriodDropdownLabel = (period) => {
    if (!period) return 'Period';

    const name = period.name || 'Period';
    const monthYearRange = formatPeriodShortMonthYearRange(period);
    if (!monthYearRange) return name;

    return `${name} · ${monthYearRange}`;
};

/** End is on or after start, and at most one year after start (same week next year). */
export const isValidEndForStart = (sy, sw, ey, ew) => {
    if (!sy || sw == null || Number.isNaN(sw) || !ey || ew == null || Number.isNaN(ew)) return false;
    if (ey === sy) return ew >= sw && ew <= 53;
    if (ey === sy + 1) return ew >= 1 && ew <= sw;
    return false;
};

const addDays = (date, days) => {
    const next = new Date(date.getTime());
    next.setDate(next.getDate() + days);
    return next;
};

const getIsoYearWeek = (date) => {
    const week = getWeekNumber(date);
    const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    utc.setUTCDate(utc.getUTCDate() + 4 - (utc.getUTCDay() || 7));
    return { year: utc.getUTCFullYear(), week };
};

const formatYearWeekOption = (year, week) => {
    const startDate = getDateFromWeek(year, week);
    return {
        value: `${year}|${week}`,
        label: `${year} · Week ${week} (${startDate})`
    };
};

const buildWeekOptionsFromDates = (fromDate, toDate, extraKeys = []) => {
    const seen = new Set();
    const options = [];

    const startIso = getIsoYearWeek(fromDate);
    const endIso = getIsoYearWeek(toDate);
    const startRange = getWeekDateRange(startIso.year, startIso.week);
    const endRange = getWeekDateRange(endIso.year, endIso.week);
    if (!startRange || !endRange) return [];

    let cursor = new Date(startRange.startDate.getTime());
    const last = endRange.startDate;
    while (cursor.getTime() <= last.getTime()) {
        const { year, week } = getIsoYearWeek(cursor);
        const key = `${year}|${week}`;
        if (!seen.has(key)) {
            seen.add(key);
            options.push(formatYearWeekOption(year, week));
        }
        cursor = addDays(cursor, 7);
    }

    extraKeys.forEach((extra) => {
        if (!extra || seen.has(extra)) return;
        const { year, week } = parseYearWeekKey(extra);
        if (!year || week == null || Number.isNaN(week)) return;
        seen.add(extra);
        options.push(formatYearWeekOption(year, week));
    });

    options.sort((a, b) => {
        const pa = parseYearWeekKey(a.value);
        const pb = parseYearWeekKey(b.value);
        return (pa.year * 100 + pa.week) - (pb.year * 100 + pb.week);
    });
    return options;
};

export const parseYearWeekKey = (key) => {
    const [y, w] = String(key).split('|').map(Number);
    return { year: y, week: w };
};

/** @param {number} calendarYear */
export const getDateFromWeek = (calendarYear, week) => {
    const firstJan = new Date(calendarYear, 0, 1);
    const firstJanDay = firstJan.getDay();
    const daysToFirstMonday = (8 - firstJanDay) % 7;
    const daysToAdd = daysToFirstMonday + (week - 1) * 7;
    const targetDate = new Date(calendarYear, 0, 1 + daysToAdd);
    return targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Start weeks: 8 weeks before today through 2 years after today. */
export const buildCalendarWeekYearOptions = (extraKeys = []) => {
    const now = new Date();
    const to = new Date(now.getTime());
    to.setFullYear(to.getFullYear() + 2);
    return buildWeekOptionsFromDates(addDays(now, -8 * 7), to, extraKeys);
};

/** End weeks: from the selected start through start + 1 year. */
export const buildEndKeyOptionsForStart = (startKey, extraKeys = []) => {
    const { year: sy, week: sw } = parseYearWeekKey(startKey);
    if (!sy || sw == null || Number.isNaN(sw)) return [];

    const startRange = getWeekDateRange(sy, sw);
    if (!startRange) return [];

    const endLimit = new Date(startRange.startDate.getTime());
    endLimit.setFullYear(endLimit.getFullYear() + 1);
    return buildWeekOptionsFromDates(startRange.startDate, endLimit, extraKeys)
        .filter((option) => {
            const { year, week } = parseYearWeekKey(option.value);
            return isValidEndForStart(sy, sw, year, week);
        });
};

export const getDefaultWeekKey = () => {
    const { year, week } = getIsoYearWeek(new Date());
    return `${year}|${week}`;
};

export const getDefaultNewPeriodState = () => {
    const k = getDefaultWeekKey();
    return {
        name: '',
        startKey: k,
        endKey: k,
        color: pickDefaultPeriodColor()
    };
};

export const getPeriodEditorStateFromPeriod = (period) => {
    if (!period) return getDefaultNewPeriodState();

    const startYear = period.startWeekYear ?? period.start_week_year ?? period.year;
    const startWeek = period.startWeek ?? period.start_week;
    const endWeek = period.endWeek ?? period.end_week;
    const endYear = period.endWeekYear ?? period.end_week_year
        ?? inferEndWeekYear(startYear, startWeek, endWeek);

    return {
        name: period.name || '',
        startKey: `${startYear}|${startWeek}`,
        endKey: `${endYear}|${endWeek}`,
        color: period.color || pickDefaultPeriodColor()
    };
};

export const formatPeriodBoundsLabel = (period) => {
    const sy = period.startWeekYear ?? period.start_week_year ?? period.year;
    const sw = period.startWeek ?? period.start_week;
    const ew = period.endWeek ?? period.end_week;
    const ey = period.endWeekYear ?? period.end_week_year ?? inferEndWeekYear(sy, sw, ew);
    if (sy != null && sw != null && ew != null) {
        if (ey != null && ey === sy) {
            return `${sy} W${sw} → W${ew}`;
        }
        if (ey != null) {
            return `${sy} W${sw} → ${ey} W${ew}`;
        }
    }
    if (sw != null && ew != null) {
        return `Week ${sw} to Week ${ew}`;
    }
    return '';
};

export const validateNewPeriod = (newPeriod) => {
    const { year: sy, week: sw } = parseYearWeekKey(newPeriod.startKey);
    const { year: ey, week: ew } = parseYearWeekKey(newPeriod.endKey);

    if (!newPeriod.name?.trim() || !sy || sw == null || Number.isNaN(sw)
        || !ey || ew == null || Number.isNaN(ew)) {
        return { valid: false, error: 'Please enter a period name and valid start/end weeks.' };
    }

    if (ey < sy || ey > sy + 1) {
        return { valid: false, error: 'Period end must fall in the start year or the following calendar year.' };
    }

    if (!isValidEndForStart(sy, sw, ey, ew)) {
        if (ey === sy + 1) {
            return { valid: false, error: `Period end in ${sy + 1} must be week 1–${sw}.` };
        }
        return { valid: false, error: 'Period end must be on or after the start week in the same calendar year.' };
    }

    return {
        valid: true,
        payload: {
            name: newPeriod.name.trim(),
            startWeek: sw,
            endWeek: ew,
            startWeekYear: sy,
            endWeekYear: ey,
            year: sy,
            color: newPeriod.color
        }
    };
};

export const normalizePeriodFromApi = (p) => {
    const startWeekYear = p.startWeekYear ?? p.start_week_year ?? p.year;
    const endWeekYear = p.endWeekYear ?? p.end_week_year;
    return {
        ...p,
        startWeekYear,
        endWeekYear,
        year: p.year ?? startWeekYear
    };
};

const weekTupleKey = (year, week) => Number(year) * 100 + Number(week);

const getPeriodWeekField = (period, camelKey, snakeKey) => {
    const value = Number(period?.[camelKey] ?? period?.[snakeKey]);
    return Number.isFinite(value) ? value : null;
};

/**
 * Calendar year for a week number, using only the attached period's week/year bounds.
 * Same rule as ScheduleEntry.getCalendarYearForWeekInResolvedPeriod.
 */
export const getCalendarYearForWeekInPeriod = (weekNumber, period) => {
    if (!period || !Number.isFinite(Number(weekNumber))) return null;

    const startWeek = getPeriodWeekField(period, 'startWeek', 'start_week');
    const endWeek = getPeriodWeekField(period, 'endWeek', 'end_week');
    const startWeekYear = getPeriodWeekField(period, 'startWeekYear', 'start_week_year')
        ?? getPeriodWeekField(period, 'year', 'year');
    const endWeekYear = getPeriodWeekField(period, 'endWeekYear', 'end_week_year')
        ?? (startWeekYear != null && startWeek != null && endWeek != null
            ? inferEndWeekYear(startWeekYear, startWeek, endWeek)
            : null);

    if (startWeek == null || endWeek == null || startWeekYear == null || endWeekYear == null) {
        return null;
    }

    if (startWeekYear === endWeekYear) {
        return startWeekYear;
    }

    if (startWeek > endWeek) {
        if (weekNumber >= startWeek) return startWeekYear;
        if (weekNumber <= endWeek) return endWeekYear;
    }

    return startWeekYear;
};

/**
 * Clip a period to the earliest and latest week used by a draft's schedule entries.
 * Years come from `sourcePeriod` only — never from entry.year.
 */
export const getDraftPeriodBoundsFromEntries = (entries, sourcePeriod) => {
    if (!sourcePeriod) return null;

    let min = null;
    let max = null;

    for (const entry of entries || []) {
        const weekSeries = entry?.weekSeries ?? entry?.week_series ?? '';
        const weeks = parseWeekSeries(weekSeries);

        for (const week of weeks) {
            const year = getCalendarYearForWeekInPeriod(week, sourcePeriod);
            if (year == null) continue;

            const resolved = { week, year };
            const key = weekTupleKey(resolved.year, resolved.week);
            if (!min || key < weekTupleKey(min.year, min.week)) min = resolved;
            if (!max || key > weekTupleKey(max.year, max.week)) max = resolved;
        }
    }

    if (!min || !max) return null;

    return {
        startWeek: min.week,
        startWeekYear: min.year,
        endWeek: max.week,
        endWeekYear: max.year
    };
};
